import { Star } from "lucide-react"
import { Team } from "@/model/Team"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

interface TeamCardProps {
  team: Team
}

const POSITION_ORDER: Record<string, number> = {
  QB: 0,
  RB: 1,
  WR: 2,
  TE: 3,
  DST: 4,
  K: 5,
}

// Option 1: Two-Column Grid Layout
export function TeamCardOption1({ team }: TeamCardProps) {
  const sortedPlayers = [...team.players].sort(
    (a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position]
  )

  return (
    <Card id={`team-${team.id}`} className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{team.name} (2-Column)</CardTitle>
          <div className="text-right">
            <p className="text-2xl font-bold">{team.score.toFixed(1)}</p>
            <p className="text-sm text-muted-foreground">total points</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* MVP Player */}
          <div className="rounded-lg bg-accent/50 p-3">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-muted-foreground">MVP</span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div>
                <p className="font-medium">{team.mvp.name}</p>
                <p className="text-sm text-muted-foreground">{team.mvp.position}</p>
              </div>
              <p className="text-lg font-bold">
                {(team.mvp.points * 1.5).toFixed(1)}
              </p>
            </div>
          </div>

          {/* All Players in 2-Column Grid */}
          <div className="grid grid-cols-2 gap-2">
            {sortedPlayers.map((player) => (
              <div key={player.id} className="flex items-center justify-between p-2 rounded border bg-card/50">
                <div className="flex items-center space-x-1 min-w-0 flex-1">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">
                    {player.position}
                  </span>
                  <span className="text-xs truncate">{player.name}</span>
                  {player.id === team.mvp.id && (
                    <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                  )}
                </div>
                <span className="text-xs font-medium shrink-0 ml-1">
                  {player.id === team.mvp.id
                    ? (player.points * 1.5).toFixed(1)
                    : player.points.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Option 2: Compact Single-Column Layout
export function TeamCardOption2({ team }: TeamCardProps) {
  const sortedPlayers = [...team.players].sort(
    (a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position]
  )

  return (
    <Card id={`team-${team.id}`} className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{team.name} (Compact)</CardTitle>
          <div className="text-right">
            <p className="text-2xl font-bold">{team.score.toFixed(1)}</p>
            <p className="text-sm text-muted-foreground">total points</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* MVP Player */}
          <div className="rounded-lg bg-accent/50 p-2">
            <div className="flex items-center gap-2">
              <Star className="h-3 w-3 text-yellow-500" />
              <span className="text-xs font-medium text-muted-foreground">MVP</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{team.mvp.name}</p>
                <p className="text-xs text-muted-foreground">{team.mvp.position}</p>
              </div>
              <p className="text-sm font-bold shrink-0 ml-2">
                {(team.mvp.points * 1.5).toFixed(1)}
              </p>
            </div>
          </div>

          {/* All Players in Compact Single Column */}
          <div className="space-y-1">
            {sortedPlayers.map((player) => (
              <div key={player.id} className="flex items-center justify-between py-1 px-2 rounded bg-card/30">
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <span className="text-xs font-medium text-muted-foreground shrink-0 w-8">
                    {player.position}
                  </span>
                  <span className="text-xs truncate flex-1">{player.name}</span>
                  {player.id === team.mvp.id && (
                    <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                  )}
                </div>
                <span className="text-xs font-medium shrink-0 ml-2">
                  {player.id === team.mvp.id
                    ? (player.points * 1.5).toFixed(1)
                    : player.points.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Single-line compact layout (most compact option)
export function TeamCardCompact({ team }: TeamCardProps) {
  const sortedPlayers = [...team.players].sort(
    (a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position]
  )

  return (
    <Card id={`team-${team.id}`} className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{team.name}</CardTitle>
          <div className="text-right">
            <p className="text-xl font-bold">{team.score.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground">pts</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {/* MVP Player - prominent card style */}
          <div className="rounded-lg bg-accent/50 p-2">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-muted-foreground">MVP</span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{team.mvp.name}</p>
                <p className="text-sm text-muted-foreground">{team.mvp.position} • {team.mvp.nflTeam}</p>
              </div>
              <p className="text-lg font-bold shrink-0 ml-2">
                {(team.mvp.points * 1.5).toFixed(1)}
              </p>
            </div>
          </div>

          {/* All Players in Single-Line Compact Layout */}
          {sortedPlayers.map((player) => (
            <div key={player.id} className="flex items-center justify-between py-1 px-2 rounded bg-card/30">
              <div className="flex items-center space-x-1.5 min-w-0 flex-1">
                <span className="text-xs font-medium text-muted-foreground shrink-0 w-8">
                  {player.position}
                </span>
                <span className="text-xs truncate flex-1">{player.name}</span>
                {player.id === team.mvp.id && (
                  <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                )}
                <span className="text-xs text-muted-foreground shrink-0">
                  {player.nflTeam}
                </span>
              </div>
              <span className="text-xs font-medium shrink-0 ml-2">
                {player.id === team.mvp.id
                  ? (player.points * 1.5).toFixed(1)
                  : player.points.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// Current implementation (two-line layout)
export function TeamCard({ team }: TeamCardProps) {
  const sortedPlayers = [...team.players].sort(
    (a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position]
  )

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{team.name}</CardTitle>
          <div className="text-right">
            <p className="text-2xl font-bold">{team.score.toFixed(1)}</p>
            <p className="text-sm text-muted-foreground">total points</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* MVP Player */}
          <div className="rounded-lg bg-accent/50 p-2">
            <div className="flex items-center gap-2">
              <Star className="h-3 w-3 text-yellow-500" />
              <span className="text-xs font-medium text-muted-foreground">MVP</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{team.mvp.name}</p>
                <p className="text-xs text-muted-foreground">{team.mvp.position} • {team.mvp.nflTeam}</p>
              </div>
              <p className="text-sm font-bold shrink-0 ml-2">
                {(team.mvp.points * 1.5).toFixed(1)}
              </p>
            </div>
          </div>

          {/* All Players in Two-Line Layout */}
          <div className="space-y-1">
            {sortedPlayers.map((player) => (
              <div key={player.id} className="p-1.5 rounded bg-card/30">
                {/* First line: Player name and points */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5 min-w-0 flex-1">
                    <span className="text-xs truncate font-medium">{player.name}</span>
                    {player.id === team.mvp.id && (
                      <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                    )}
                  </div>
                  <span className="text-xs font-medium shrink-0">
                    {player.id === team.mvp.id
                      ? (player.points * 1.5).toFixed(1)
                      : player.points.toFixed(1)}
                  </span>
                </div>
                {/* Second line: Position and NFL team */}
                <div className="flex items-center space-x-1.5 mt-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {player.position}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {player.nflTeam}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
